<template>
    <section class="w-full mb-4 px-2 sm:px-4 md:px-6 lg:px-8">
        <div class="flex items-center justify-center py-4">
            <div class="w-full max-w-2xl">
                <div 
                    @click="!isUIDisabled && triggerFileInput()"
                    @drop.prevent="!isUIDisabled && handleDrop($event)"
                    @dragover.prevent="!isUIDisabled && (isDragging = true)"
                    @dragleave.prevent="!isUIDisabled && (isDragging = false)"
                    :class="['drag-drop-area', { 'dragging': isDragging, 'disabled': isUIDisabled }]"
                    class="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center transition-all"
                    :style="{ cursor: isUIDisabled ? 'not-allowed' : 'pointer' }"
                >
                    <i class="pi pi-cloud-upload text-6xl text-gray-400 mb-4"></i>
                    <p class="text-gray-600 dark:text-gray-400 text-center mb-2">
                        Arrastra y suelta tu archivo CSV aquí
                    </p>
                    <p class="text-gray-500 dark:text-gray-500 text-sm">
                        o haz clic para seleccionar
                    </p>
                    <input 
                        ref="fileInput"
                        type="file" 
                        accept=".csv" 
                        @change="handleFileInput"
                        class="hidden"
                        :disabled="isUIDisabled"
                    />
                </div>
            </div>
        </div>

        <div v-if="csvData.length > 0">
            <Divider />
            
            <Message severity="info" :closable="false" class="mb-4">
                <div>
                    <strong>Archivo:</strong> {{ fileName }} <br>
                    <strong>Registros filtrados encontrados:</strong> {{ filteredCsvData.length }} <br>
                    <strong>Columnas encontradas:</strong> {{ allFoundColumns.length }} de {{ requiredColumns.length }}
                </div>
            </Message>

            <Message v-if="missingColumns.length > 0" severity="warn" :closable="false" class="mb-4">
                <div>
                    <strong>Columnas faltantes:</strong> {{ missingColumns.join(', ') }}
                </div>
            </Message>

            <div class="table-responsive-container">
                <DataTable 
                    v-if="missingColumns.length === 0"
                    v-model:selection="selectedRows"
                    :value="filteredCsvData"
                    stripedRows
                    responsiveLayout="scroll"
                    class="mb-4 bulk-upload-table"
                    dataKey="Folio"
                    :loading="isUIDisabled"
                    :scrollable="true"
                    scrollHeight="flex"
                >
                <Column selectionMode="multiple" headerStyle="width: 50px; min-width: 50px;"></Column>
                <Column 
                    v-for="(header, index) in foundColumns" 
                    :key="index"
                    :field="header" 
                    :header="header"
                    :sortable="true"
                >
                    <template v-if="header === 'Concepto'" #body="slotProps">
                        <div 
                            @click="startEditingConcept(slotProps.index)"
                            class="editable-cell"
                            :title="'Click para editar'"
                        >
                            <span v-if="editingConceptIndex !== slotProps.index">
                                {{ slotProps.data[header] }}
                            </span>
                            <input 
                                v-else
                                v-model="editingConceptValue"
                                @blur="saveConceptEdit(slotProps.index)"
                                @keyup.enter="saveConceptEdit(slotProps.index)"
                                @keyup.esc="cancelConceptEdit"
                                class="concept-input"
                                ref="conceptInput"
                                type="text"
                            />
                        </div>
                    </template>
                    <template v-else-if="header === 'Folio'" #body="slotProps">
                        <div 
                            @click="startEditingFolio(slotProps.index)"
                            class="editable-cell"
                            :title="'Click para editar folio'"
                        >
                            <span v-if="editingFolioIndex !== slotProps.index">
                                {{ slotProps.data[header] }}
                            </span>
                            <input 
                                v-else
                                v-model="editingFolioValue"
                                @blur="saveFolioEdit(slotProps.index)"
                                @keyup.enter="saveFolioEdit(slotProps.index)"
                                @keyup.esc="cancelFolioEdit"
                                class="folio-input"
                                ref="folioInput"
                                type="text"
                            />
                        </div>
                    </template>
                </Column>
                
                <!-- Legal Name Dropdown Column (Provisional) -->
                <Column header="Razón Social" headerStyle="width: 180px; min-width: 180px;">
                    <template #body="slotProps">
                        <Dropdown 
                            :modelValue="getRowSelection(slotProps.data.Folio, 'legalName')"
                            :options="legalNames"
                            optionLabel="name"
                            placeholder="Selecciona razón social"
                            class="w-full dropdown-cell dropdown-legal-name"
                            :filter="true"
                            filterPlaceholder="Buscar razón social"
                            :disabled="isUIDisabled"
                            @update:modelValue="updateRowSelection(slotProps.data.Folio, 'legalName', $event)"
                        />
                    </template>
                </Column>
                
                <!-- Cliente Column -->
                <Column header="Cliente" headerStyle="width: 150px; min-width: 150px;">
                    <template #body="slotProps">
                        <Dropdown 
                            :modelValue="getRowSelection(slotProps.data.Folio, 'client')"
                            :options="clientOptions"
                            optionLabel="name"
                            placeholder="Selecciona un cliente"
                            class="w-full dropdown-cell dropdown-client"
                            :filter="true"
                            filterPlaceholder="Buscar cliente"
                            :disabled="isUIDisabled"
                            @update:modelValue="updateRowSelection(slotProps.data.Folio, 'client', $event)"
                        />
                    </template>
                </Column>

                <!-- Tipo Column -->
                <Column header="Tipo" headerStyle="width: 120px; min-width: 120px;">
                    <template #body="slotProps">
                        <Dropdown 
                            :modelValue="getRowSelection(slotProps.data.Folio, 'type')"
                            :options="typeOptions"
                            optionLabel="name"
                            placeholder="Selecciona un tipo"
                            class="w-full dropdown-cell dropdown-type"
                            :disabled="isUIDisabled"
                            @update:modelValue="updateRowSelection(slotProps.data.Folio, 'type', $event)"
                        />
                    </template>
                </Column>

                <!-- Comisión Column -->
                <Column header="Comisión" headerStyle="width: 180px; min-width: 180px;">
                    <template #body="slotProps">
                        <div class="commission-container">
                            <Dropdown 
                                :modelValue="getRowSelection(slotProps.data.Folio, 'branch')"
                                :options="branchOptions"
                                optionLabel="name"
                                placeholder="Sin comisión"
                                class="w-full dropdown-cell dropdown-branch mb-2"
                                :disabled="isUIDisabled"
                                @update:modelValue="updateRowSelection(slotProps.data.Folio, 'branch', $event)"
                            />
                            <div 
                                v-if="getRowSelection(slotProps.data.Folio, 'branch')?.id !== null"
                                class="commission-percentage-input"
                            >
                                <InputNumber
                                    :modelValue="getRowSelection(slotProps.data.Folio, 'commissionPercentage')"
                                    @update:modelValue="updateRowSelection(slotProps.data.Folio, 'commissionPercentage', $event)"
                                    mode="decimal"
                                    :min="0"
                                    :max="100"
                                    :maxFractionDigits="2"
                                    suffix="%"
                                    placeholder="% Comisión"
                                    class="w-full"
                                    :disabled="isUIDisabled"
                                />
                            </div>
                        </div>
                    </template>
                </Column>

                <Column header="Acciones" headerStyle="width: 80px; min-width: 80px;">
                    <template #body="slotProps">
                        <Button 
                            icon="pi pi-trash" 
                            class="p-button-rounded p-button-danger p-button-text delete-row-btn"
                            @click="deleteRow(slotProps.index)"
                            title="Eliminar registro"
                            :disabled="isUIDisabled"
                        />
                    </template>
                </Column>
            </DataTable>
            </div>

            <div class="flex gap-3 mt-4 justify-center">
                <Button 
                    v-if="selectedRows.length > 0"
                    :label="`Eliminar Seleccionados (${selectedRows.length})`"
                    icon="pi pi-trash" 
                    @click="deleteSelected"
                    class="delete-btn"
                    :disabled="isUIDisabled"
                />
                <Button 
                    v-if="selectedRows.length > 0"
                    :label="`Actualizar Concepto (${selectedRows.length})`"
                    icon="pi pi-pencil" 
                    @click="showBulkEditDialog"
                    class="bulk-edit-btn"
                    :disabled="isUIDisabled"
                />
                <Button 
                    label="Procesar Facturas" 
                    icon="pi pi-check" 
                    @click="processInvoices"
                    :disabled="isUIDisabled"
                    class="process-btn"
                />
                <Button 
                    label="Limpiar" 
                    icon="pi pi-times" 
                    @click="clearData"
                    :disabled="isUIDisabled"
                    class="clear-btn"
                />
            </div>
        </div>

        <div v-else class="mt-4">
            <Message severity="warn" :closable="false">
                No se ha cargado ningún archivo CSV. Por favor, selecciona un archivo para continuar.
            </Message>
        </div>

        <!-- Confirmation Dialog -->
        <ConfirmDialog />

        <!-- Bulk Edit Dialog -->
        <Dialog 
            v-model:visible="bulkEditDialogVisible" 
            modal 
            header="Actualizar Concepto Masivo"
            :style="{width: '450px'}"
            :closable="!isUIDisabled"
        >
            <div class="flex flex-col gap-4">
                <div>
                    <label for="bulkDescription" class="block text-sm font-medium mb-2">
                        Nuevo concepto para {{ selectedRows.length }} factura(s):
                    </label>
                    <InputText 
                        id="bulkDescription"
                        v-model="bulkEditDescription" 
                        placeholder="Ingresa el nuevo concepto"
                        class="w-full"
                        :disabled="isUIDisabled"
                        @keyup.enter="confirmBulkEdit"
                        autofocus
                    />
                </div>
                <div class="text-sm text-gray-600 dark:text-gray-400">
                    <strong>Facturas seleccionadas:</strong>
                    <div class="mt-1 max-h-32 overflow-y-auto">
                        <span 
                            v-for="row in selectedRows" 
                            :key="row.Folio"
                            class="inline-block bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded text-xs mr-1 mb-1"
                        >
                            {{ row.Folio }}
                        </span>
                    </div>
                </div>
            </div>
            <template #footer>
                <Button 
                    label="Cancelar" 
                    icon="pi pi-times" 
                    @click="bulkEditDialogVisible = false"
                    class="p-button-text"
                    :disabled="isUIDisabled"
                />
                <Button 
                    label="Actualizar" 
                    icon="pi pi-check" 
                    @click="confirmBulkEdit"
                    :disabled="isUIDisabled || !bulkEditDescription.trim()"
                />
            </template>
        </Dialog>
    </section>
</template>

<script>
import axiosInstance from '../axiosInstance';

/**
 * Component for bulk invoice upload from CSV file
 * Allows importing multiple invoices with automatic filtering by Estado "Vigente" and Tipo "Factura"
 */
// Constants for better maintainability
const REQUIRED_COLUMNS = ['Fecha', 'Folio', 'RFC', 'Concepto', 'Subtotal', 'IVA'];
const COLUMN_MAPPING = {
    'Impuesto Trasladado IVA': 'IVA',
    'Primer Concepto': 'Concepto'
};
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const TOAST_GROUP = 'topr';
const DEFAULT_TYPE_ID = 2;

export default {
    data() {
        return {
            // Parsed CSV data
            csvData: [],
            // Original CSV headers
            headers: [],
            // Name of the loaded file
            fileName: '',
            // Controls if buttons are disabled during processing
            buttonDisabled: false,
            // Controls if API operations are in progress (blocks UI interactions)
            apiOperationInProgress: false,
            // Bulk edit dialog state
            bulkEditDialogVisible: false,
            bulkEditDescription: '',
            // Required columns for invoice processing
            requiredColumns: REQUIRED_COLUMNS,
            // Mapping of CSV column names to internal names
            columnMapping: COLUMN_MAPPING,
            // Controls drag state for visual feedback
            isDragging: false,
            // Selected rows for deletion
            selectedRows: [],
            // Data from API for massive invoices
            legalNames: [],
            clients: [],
            branches: [],
            types: [],
            // Concept editing state
            editingConceptIndex: null,
            editingConceptValue: '',
            editingConceptCancelled: false,
            // Folio editing state
            editingFolioIndex: null,
            editingFolioValue: '',
            editingFolioCancelled: false,
            // Row selections for dropdowns (indexed by row Folio)
            rowSelections: {}
        }
    },
    computed: {
        /**
         * Returns required columns that exist in the CSV and should be displayed in the table
         * Considers both direct names and mapped ones, but excludes RFC (used only for matching)
         * @returns {Array<string>} Array of found column names for display
         */
        foundColumns() {
            // Returns only required columns that exist in the CSV, excluding RFC
            return this.requiredColumns.filter(col => {
                // Exclude RFC from visible columns
                if (col === 'RFC') return false;
                
                return this.headers.includes(col) || 
                       Object.entries(this.columnMapping).some(([csvName, internalName]) =>
                           internalName === col && this.headers.includes(csvName)
                       );
            });
        },
        /**
         * Returns required columns that do NOT exist in the CSV
         * Useful for showing warnings to the user
         * @returns {Array<string>} Array of missing column names
         */
        missingColumns() {
            // Returns required columns that do NOT exist in the CSV
            return this.requiredColumns.filter(col => {
                return !this.headers.includes(col) && 
                       !Object.entries(this.columnMapping).some(([csvName, internalName]) => 
                           internalName === col && this.headers.includes(csvName)
                       );
            });
        },
        /**
         * Returns all required columns that exist in the CSV (including RFC for internal use)
         * @returns {Array<string>} Array of all found column names including RFC
         */
        allFoundColumns() {
            // Reuse foundColumns logic and add RFC if it exists
            const found = [...this.foundColumns];
            if (this.headers.includes('RFC') || 
                Object.entries(this.columnMapping).some(([csvName, internalName]) =>
                    internalName === 'RFC' && this.headers.includes(csvName)
                )) {
                found.push('RFC');
            }
            return found;
        },
        /**
         * Returns CSV data filtered by:
         * 1. Estado = "Vigente" and Tipo = "Factura"
         * 2. Includes all required columns (including RFC for internal use)
         * @returns {Array<Object>} Array of objects with filtered data
         */
        filteredCsvData() {
            // Filter data by Estado = "Vigente" and Tipo = "Factura"
            const filtered = this.csvData.filter(row => {
                return row['Estado'] === 'Vigente' && row['Tipo'] === 'Factura';
            });
            
            // Include all found columns (including RFC for internal use)
            return filtered.map(row => {
                const filteredRow = {};
                // Add visible columns
                this.foundColumns.forEach(column => {
                    filteredRow[column] = row[column] || '';
                });
                // Add RFC for internal matching (not visible in table)
                if (row['RFC']) {
                    filteredRow['RFC'] = row['RFC'];
                }
                return filteredRow;
            });
        },

        /**
         * Returns clients list with default "Selecciona un cliente" option
         * @returns {Array<Object>} Array of client options
         */
        clientOptions() {
            return [
                { id: null, name: 'Selecciona un cliente' },
                ...this.clients
            ];
        },

        /**
         * Returns types list (no default, type with id 2 is selected by default in initialization)
         * @returns {Array<Object>} Array of type options
         */
        typeOptions() {
            return this.types;
        },

        /**
         * Returns branches list with default "Sin comisión" option
         * @returns {Array<Object>} Array of branch options
         */
        branchOptions() {
            return [
                { id: null, name: 'Sin comisión' },
                ...this.branches
            ];
        },

        /**
         * Returns true if UI components should be disabled due to API operations
         * @returns {boolean} True if components should be disabled
         */
        isUIDisabled() {
            return this.buttonDisabled || this.apiOperationInProgress;
        }
    },
    methods: {
        /**
         * Triggers the hidden file input click
         */
        triggerFileInput() {
            this.$refs.fileInput.click();
        },

        /**
         * Handles file selection from input
         * @param {Event} event - File input change event
         */
        handleFileInput(event) {
            const file = event.target.files[0];
            if (file) {
                this.processFile(file);
            }
        },

        /**
         * Handles file drop event
         * @param {DragEvent} event - Drop event
         */
        handleDrop(event) {
            this.isDragging = false;
            const file = event.dataTransfer.files[0];
            if (file && file.name.endsWith('.csv')) {
                this.processFile(file);
            } else {
                this.$toast.add({ severity: 'warn', summary: 'Aviso',
                    detail: 'Por favor, selecciona un archivo CSV válido', life: 5000, group: 'topr' });
            }
        },

        /**
         * Processes the selected or dropped file with size validation
         * @param {File} file - CSV file to process (max 5MB)
         */
        processFile(file) {
            if (file.size > MAX_FILE_SIZE) {
                this.$toast.add({ 
                    severity: 'error', 
                    summary: 'Error',
                    detail: 'El archivo es demasiado grande. Máximo 5MB', 
                    life: 5000, 
                    group: TOAST_GROUP 
                });
                return;
            }

            this.fileName = file.name;
            this.readCSV(file);
        },

        /**
         * Reads CSV file content using FileReader API
         * Uses ISO-8859-1 encoding to properly read Spanish characters
         * @param {File} file - CSV file object to read
         */
        readCSV(file) {
            const reader = new FileReader();
            
            reader.onload = (e) => {
                const text = e.target.result;
                this.parseCSV(text);
            };
            
            reader.onerror = (error) => {
                console.error('Error al leer el archivo:', error);
                this.$toast.add({ severity: 'error', summary: 'Error',
                    detail: 'Error al leer el archivo CSV', life: 5000, group: 'topr' });
            };
            
            // Try reading with ISO-8859-1 (Latin-1) encoding, which is common for CSV files with Spanish characters
            reader.readAsText(file, 'ISO-8859-1');
        },

        /**
         * Parses CSV content into JavaScript objects
         * - Extracts headers from the first line
         * - Converts each line into an object with headers as keys
         * - Applies column mapping, date formatting, folio prefix, and currency formatting
         * @param {string} text - Complete CSV file content
         */
        parseCSV(text) {
            const lines = text.split('\n').filter(line => line.trim() !== '');
            
            if (lines.length === 0) {
                this.$toast.add({ severity: 'warn', summary: 'Aviso',
                    detail: 'El archivo CSV está vacío', life: 5000, group: 'topr' });
                return;
            }

            // First line contains headers
            this.headers = this.parseCSVLine(lines[0]);
            
            // Parse remaining lines as data
            this.csvData = [];
            for (let i = 1; i < lines.length; i++) {
                const values = this.parseCSVLine(lines[i]);
                const row = {};
                
                this.headers.forEach((header, index) => {
                    // Apply column name mapping
                    const mappedHeader = this.columnMapping[header] || header;
                    let value = values[index] || '';
                    
                    // Note: Dates are kept in their original format from CSV
                    // Users should ensure dates are in DD/MM/YYYY format before uploading
                    
                    // Add "B" prefix to Folio column
                    if (mappedHeader === 'Folio' && value) {
                        value = 'B' + value;
                    }
                    
                    // Format numeric values for money columns
                    if (['Subtotal', 'IVA'].includes(mappedHeader) && value) {
                        value = this.formatCurrency(value);
                    }
                    
                    row[mappedHeader] = value;
                });
                
                this.csvData.push(row);
            }
            
            console.log('Datos parseados:', this.csvData);
            console.log('Columnas encontradas:', this.foundColumns);
            console.log('Columnas faltantes:', this.missingColumns);
            console.log('Registros después de filtrar (Vigente y Factura):', this.filteredCsvData.length);

            // Fetch massive invoices data from API
            this.fetchMassiveInvoicesData();
        },

        /**
         * Fetches configuration data from API for invoice processing
         * Retrieves legal names, clients, branches, and types
         * @async
         * @throws {Error} When API request fails
         */
        async fetchMassiveInvoicesData() {
            this.apiOperationInProgress = true;
            
            try {
                const response = await axiosInstance.post("/api/massive_invoices_data", {
                    token: localStorage.getItem('token')
                });

                if (response.status === 200) {
                    const { legalNames, clients, branches, types } = response.data;
                    
                    this.legalNames = legalNames || [];
                    this.clients = clients || [];
                    this.branches = branches || [];
                    this.types = types || [];
                    
                    // Initialize row selections with defaults after data is loaded
                    this.$nextTick(() => {
                        this.initializeRowSelections();
                    });
                }
            } catch (error) {
                console.error('Error al obtener datos de facturas masivas:', error);
                this.$toast.add({ 
                    severity: 'error', 
                    summary: 'Error',
                    detail: 'Error al obtener datos del servidor', 
                    life: 5000, 
                    group: 'topr' 
                });
            } finally {
                this.apiOperationInProgress = false;
            }
        },

        /**
         * Formats a numeric value as currency with thousands separator and 2 decimals
         * @param {string|number} value - Numeric value to format
         * @returns {string} Formatted value (e.g., "1,000.00")
         */
        formatCurrency(value) {
            // Remove any existing commas and convert to number
            const numValue = parseFloat(String(value).replace(/,/g, ''));

            // Check if it's a valid number
            if (isNaN(numValue)) {
                return value;
            }

            // Format with 2 decimals and thousands separator
            return numValue.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            });
        },


        /**
         * Parses individual CSV line
         * Properly handles quoted values that may contain commas
         * @param {string} line - CSV line to parse
         * @returns {Array<string>} Array of parsed values
         */
        parseCSVLine(line) {
            const result = [];
            let current = '';
            let inQuotes = false;
            
            for (let i = 0; i < line.length; i++) {
                const char = line[i];
                
                if (char === '"') {
                    inQuotes = !inQuotes;
                } else if (char === ',' && !inQuotes) {
                    result.push(current.trim());
                    current = '';
                } else {
                    current += char;
                }
            }
            
            result.push(current.trim());
            return result;
        },

        /**
         * Processes filtered invoices
         * Validates that:
         * - There is loaded data
         * - Required columns were found
         * - Records exist after filtering
         * Sends data to backend for saving
         */
        async processInvoices() {
            // Basic data validations
            if (this.csvData.length === 0) {
                this.$toast.add({ severity: 'warn', summary: 'Aviso',
                    detail: 'No hay datos para procesar', life: 5000, group: 'topr' });
                return;
            }
            
            if (this.allFoundColumns.length === 0) {
                this.$toast.add({ severity: 'error', summary: 'Error',
                    detail: 'No se encontró ninguna de las columnas requeridas', life: 5000, group: 'topr' });
                return;
            }
            
            if (this.filteredCsvData.length === 0) {
                this.$toast.add({ severity: 'warn', summary: 'Aviso',
                    detail: 'No hay registros con Estado "Vigente" y Tipo "Factura"', life: 5000, group: 'topr' });
                return;
            }

            // Validate that required API data is loaded
            if (!this.legalNames.length || !this.clients.length || !this.types.length) {
                this.$toast.add({ severity: 'error', summary: 'Error',
                    detail: 'Los datos necesarios no se han cargado correctamente. Recarga la página.', 
                    life: 6000, group: 'topr' });
                return;
            }

            // Pre-validate selections before processing
            const preValidationErrors = [];
            this.filteredCsvData.forEach((row, index) => {
                const folio = row.Folio;
                const selections = this.rowSelections[folio] || {};
                
                if (!selections.legalName) {
                    preValidationErrors.push(`Fila ${index + 1} (${folio}): Falta razón social`);
                }
                if (!selections.client || selections.client.id === null) {
                    preValidationErrors.push(`Fila ${index + 1} (${folio}): Falta cliente`);
                }
                if (!selections.type) {
                    preValidationErrors.push(`Fila ${index + 1} (${folio}): Falta tipo`);
                }
                if (selections.branch && selections.branch.id !== null && !selections.commissionPercentage) {
                    preValidationErrors.push(`Fila ${index + 1} (${folio}): Falta porcentaje de comisión`);
                }
            });

            if (preValidationErrors.length > 0) {
                console.error('Errores de pre-validación:', preValidationErrors);
                this.$toast.add({ 
                    severity: 'error', 
                    summary: 'Datos incompletos',
                    detail: `${preValidationErrors.length} registro(s) tienen datos faltantes. Ver consola para detalles.`, 
                    life: 8000, 
                    group: 'topr' 
                });
                return;
            }

            // Show confirmation dialog before processing
            this.$confirm.require({
                message: `¿Estás seguro de que deseas guardar ${this.filteredCsvData.length} factura(s)?`,
                header: 'Confirmar guardado',
                icon: 'pi pi-save',
                acceptLabel: 'Sí, guardar',
                rejectLabel: 'Cancelar',
                accept: () => {
                    this.proceedWithSaving();
                },
                reject: () => {
                    // User cancelled, do nothing
                }
            });
        },

        /**
         * Proceeds with the actual saving process after user confirmation
         */
        async proceedWithSaving() {
            // Build items array for backend
            const items = [];
            const errors = [];
            
            this.filteredCsvData.forEach((row, index) => {
                const folio = row.Folio;
                const selections = this.rowSelections[folio] || {};
                
                // Validate required selections
                if (!selections.legalName) {
                    errors.push(`Fila ${index + 1} (Folio: ${folio}): Debe seleccionar una razón social`);
                    return;
                }
                
                if (!selections.client || selections.client.id === null) {
                    errors.push(`Fila ${index + 1} (Folio: ${folio}): Debe seleccionar un cliente`);
                    return;
                }
                
                if (!selections.type) {
                    errors.push(`Fila ${index + 1} (Folio: ${folio}): Debe seleccionar un tipo`);
                    return;
                }
                
                // Parse numeric values
                const subtotal = parseFloat(row.Subtotal.replace(/,/g, ''));
                const tax = parseFloat(row.IVA.replace(/,/g, ''));
                
                if (isNaN(subtotal)) {
                    errors.push(`Fila ${index + 1} (Folio: ${folio}): Subtotal inválido`);
                    return;
                }
                
                if (isNaN(tax)) {
                    errors.push(`Fila ${index + 1} (Folio: ${folio}): IVA inválido`);
                    return;
                }
                
                // Build item object
                const item = {
                    serial_str: folio,
                    name: selections.legalName.name,
                    description: row.Concepto,
                    subtotal: subtotal,
                    tax: tax,
                    client: selections.client.id,
                    type: selections.type.id,
                    date: row.Fecha
                };
                
                // Add commission data if branch is selected and not "Sin comisión"
                if (selections.branch && selections.branch.id !== null) {
                    item.commission_branch_id = selections.branch.id;
                    // Convert percentage to decimal (50% = 0.5)
                    item.commission_percentage = (selections.commissionPercentage || 0) / 100;
                }
                
                items.push(item);
            });
            
            // Show validation errors if any
            if (errors.length > 0) {
                console.error('Errores de validación:', errors);
                this.$toast.add({ 
                    severity: 'error', 
                    summary: 'Errores de validación',
                    detail: `${errors.length} error(es) encontrado(s). Ver consola para detalles.`, 
                    life: 8000, 
                    group: 'topr' 
                });
                return;
            }
            
            // Save invoices to backend
            await this.saveMassiveInvoices(items);
        },

        /**
         * Saves processed invoices to backend via API
         * @param {Array} items - Array of invoice objects to save
         */
        async saveMassiveInvoices(items) {
            // Disable UI during API operation
            this.apiOperationInProgress = true;
            this.buttonDisabled = true;

            try {
                this.$toast.add({ 
                    severity: 'info', 
                    summary: 'Procesando',
                    detail: `Guardando ${items.length} facturas...`, 
                    life: 3000, 
                    group: 'topr' 
                });

                const response = await axiosInstance.post("/api/massive_invoices_save", {
                    token: localStorage.getItem('token'),
                    items: items
                });

                if (response.status === 200) {
                    this.$toast.add({ 
                        severity: 'success', 
                        summary: 'Éxito',
                        detail: `Se guardaron ${items.length} facturas correctamente`, 
                        life: 5000, 
                        group: 'topr' 
                    });

                    // Clear data after successful save
                    this.clearData();
                } else {
                    throw new Error('Respuesta inesperada del servidor');
                }
            } catch (error) {
                console.error('Error al guardar facturas:', error);
                
                let errorMessage = 'Error desconocido al guardar las facturas';
                
                // Handle specific error responses from backend
                if (error.response && error.response.data) {
                    if (typeof error.response.data === 'string') {
                        errorMessage = error.response.data;
                    } else if (error.response.data.message) {
                        errorMessage = error.response.data.message;
                    } else if (error.response.data.error) {
                        errorMessage = error.response.data.error;
                    }
                } else if (error.message) {
                    errorMessage = error.message;
                }

                this.$toast.add({ 
                    severity: 'error', 
                    summary: 'Error al guardar',
                    detail: errorMessage, 
                    life: 8000, 
                    group: 'topr' 
                });
            } finally {
                // Re-enable UI
                this.apiOperationInProgress = false;
                this.buttonDisabled = false;
            }
        },

        /**
         * Clears all loaded data and resets component state
         */
        clearData() {
            this.csvData = [];
            this.headers = [];
            this.fileName = '';
            this.selectedRows = [];
            this.rowSelections = {};
            this.editingConceptIndex = null;
            this.editingConceptValue = '';
            this.editingConceptCancelled = false;
            this.editingFolioIndex = null;
            this.editingFolioValue = '';
            this.editingFolioCancelled = false;
            this.apiOperationInProgress = false;
            this.bulkEditDialogVisible = false;
            this.bulkEditDescription = '';
            // Reset file input to allow selecting the same file again
            if (this.$refs.fileInput) {
                this.$refs.fileInput.value = '';
            }
        },

        /**
         * Deletes a single row from the filtered data
         * @param {number} index - Index of the row to delete in filteredCsvData
         */
        deleteRow(index) {
            const rowToDelete = this.filteredCsvData[index];
            const folio = rowToDelete.Folio;
            
            // Find and remove from original csvData
            const originalIndex = this.csvData.findIndex(row => row.Folio === folio);
            if (originalIndex !== -1) {
                this.csvData.splice(originalIndex, 1);
                
                // Remove from selectedRows if it was selected
                const selectedIndex = this.selectedRows.findIndex(row => row.Folio === folio);
                if (selectedIndex !== -1) {
                    this.selectedRows.splice(selectedIndex, 1);
                }
                
                // Remove from rowSelections
                if (this.rowSelections[folio]) {
                    delete this.rowSelections[folio];
                }
                
                this.$toast.add({ severity: 'success', summary: 'Éxito',
                    detail: 'Registro eliminado correctamente', life: 3000, group: 'topr' });
            }
        },

        /**
         * Deletes all selected rows
         */
        deleteSelected() {
            if (this.selectedRows.length === 0) return;
            
            // Get all Folios to delete
            const foliosToDelete = this.selectedRows.map(row => row.Folio);
            
            // Filter out selected rows from csvData
            this.csvData = this.csvData.filter(row => !foliosToDelete.includes(row.Folio));
            
            // Remove from rowSelections
            foliosToDelete.forEach(folio => {
                if (this.rowSelections[folio]) {
                    delete this.rowSelections[folio];
                }
            });
            
            this.$toast.add({ severity: 'success', summary: 'Éxito',
                detail: `${foliosToDelete.length} registro(s) eliminado(s) correctamente`, life: 3000, group: 'topr' });
            
            // Clear selection
            this.selectedRows = [];
        },

        /**
         * Shows the bulk edit dialog for updating multiple descriptions
         */
        showBulkEditDialog() {
            if (this.selectedRows.length === 0) return;
            
            this.bulkEditDescription = '';
            this.bulkEditDialogVisible = true;
        },

        /**
         * Confirms and applies bulk description update to selected rows
         */
        confirmBulkEdit() {
            if (!this.bulkEditDescription.trim()) {
                this.$toast.add({ 
                    severity: 'warn', 
                    summary: 'Aviso',
                    detail: 'El concepto no puede estar vacio', 
                    life: 3000, 
                    group: 'topr' 
                });
                return;
            }

            if (this.selectedRows.length === 0) return;

            // Get all folios to update
            const foliosToUpdate = this.selectedRows.map(row => row.Folio);
            const newDescription = this.bulkEditDescription.trim();
            let updatedCount = 0;

            // Update each selected row in csvData
            foliosToUpdate.forEach(folio => {
                const originalIndex = this.csvData.findIndex(row => row.Folio === folio);
                if (originalIndex !== -1) {
                    this.csvData[originalIndex].Concepto = newDescription;
                    updatedCount++;
                }
            });

            // Show success message
            this.$toast.add({ 
                severity: 'success', 
                summary: 'Éxito',
                detail: `Se actualizó el concepto de ${updatedCount} factura(s) a: "${newDescription}"`, 
                life: 4000, 
                group: 'topr' 
            });

            // Close dialog and clear selection
            this.bulkEditDialogVisible = false;
            this.bulkEditDescription = '';
            this.selectedRows = [];
        },

        /**
         * Starts editing a concept cell
         * @param {number} index - Index of the row to edit
         */
        startEditingConcept(index) {
            this.editingConceptIndex = index;
            this.editingConceptValue = this.filteredCsvData[index].Concepto;
            this.editingConceptCancelled = false;
            // Focus input on next tick
            this.$nextTick(() => {
                if (this.$refs.conceptInput && this.$refs.conceptInput[0]) {
                    this.$refs.conceptInput[0].focus();
                    this.$refs.conceptInput[0].select();
                }
            });
        },

        /**
         * Generic method to save edited field values
         * @param {string} fieldType - Type of field being edited ('concept' or 'folio')
         * @param {number} index - Index of the row being edited
         */
        saveFieldEdit(fieldType, index) {
            const isConceptEdit = fieldType === 'concept';
            const cancelledFlag = isConceptEdit ? this.editingConceptCancelled : this.editingFolioCancelled;
            const editingIndex = isConceptEdit ? this.editingConceptIndex : this.editingFolioIndex;
            const editingValue = isConceptEdit ? this.editingConceptValue : this.editingFolioValue;
            const fieldName = isConceptEdit ? 'Concepto' : 'Folio';

            // Don't save if editing was cancelled
            if (cancelledFlag) {
                if (isConceptEdit) {
                    this.editingConceptCancelled = false;
                } else {
                    this.editingFolioCancelled = false;
                }
                return;
            }

            // Prevent multiple executions - if not in editing mode, ignore
            if (editingIndex === null) {
                return;
            }
            
            const rowToUpdate = this.filteredCsvData[index];
            const oldValue = rowToUpdate ? rowToUpdate[fieldName] : '';
            const trimmedValue = editingValue ? editingValue.trim() : '';

            // Check if there's actually a change
            if (oldValue === trimmedValue) {
                this.resetEditingState(fieldType);
                return;
            }

            // Validate that value is not empty
            if (!trimmedValue) {
                this.$toast.add({ 
                    severity: 'warn', 
                    summary: 'Aviso',
                    detail: `El ${fieldName.toLowerCase()} no puede estar vacío`, 
                    life: 3000, 
                    group: 'topr' 
                });
                return;
            }

            // For folio edits, check for duplicates
            if (!isConceptEdit) {
                const existingFolio = this.csvData.find((row, i) => {
                    const currentRowIndex = this.csvData.findIndex(r => r.Folio === oldValue);
                    return row.Folio === trimmedValue && i !== currentRowIndex;
                });

                if (existingFolio) {
                    this.$toast.add({ 
                        severity: 'error', 
                        summary: 'Error',
                        detail: 'Ya existe un registro con este folio', 
                        life: 3000, 
                        group: 'topr' 
                    });
                    return;
                }
            }
            
            // Find in original csvData and update
            const originalIndex = this.csvData.findIndex(row => row.Folio === rowToUpdate.Folio);
            if (originalIndex !== -1) {
                this.csvData[originalIndex][fieldName] = trimmedValue;
                
                // Handle folio-specific updates
                if (!isConceptEdit) {
                    this.updateFolioReferences(oldValue, trimmedValue);
                }
                
                this.$toast.add({ 
                    severity: 'success', 
                    summary: 'Éxito',
                    detail: `${fieldName} actualizado correctamente`, 
                    life: 2000, 
                    group: 'topr' 
                });
            }
            this.resetEditingState(fieldType);
        },

        /**
         * Updates references when folio changes
         * @param {string} oldFolio - Previous folio value
         * @param {string} newFolio - New folio value
         */
        updateFolioReferences(oldFolio, newFolio) {
            // Update row selections with new folio key
            if (this.rowSelections[oldFolio]) {
                this.rowSelections[newFolio] = this.rowSelections[oldFolio];
                delete this.rowSelections[oldFolio];
            }
            
            // Update selected rows if this row was selected
            const selectedRowIndex = this.selectedRows.findIndex(row => row.Folio === oldFolio);
            if (selectedRowIndex !== -1) {
                this.selectedRows[selectedRowIndex].Folio = newFolio;
            }
        },

        /**
         * Resets editing state for a specific field type
         * @param {string} fieldType - Type of field ('concept' or 'folio')
         */
        resetEditingState(fieldType) {
            if (fieldType === 'concept') {
                this.editingConceptIndex = null;
                this.editingConceptValue = '';
            } else {
                this.editingFolioIndex = null;
                this.editingFolioValue = '';
            }
        },

        /**
         * Saves the edited concept value
         * @param {number} index - Index of the row being edited
         */
        saveConceptEdit(index) {
            this.saveFieldEdit('concept', index);
        },

        /**
         * Generic method to cancel field editing
         * @param {string} fieldType - Type of field ('concept' or 'folio')
         */
        cancelFieldEdit(fieldType) {
            if (fieldType === 'concept') {
                this.editingConceptCancelled = true;
                this.editingConceptIndex = null;
                this.editingConceptValue = '';
            } else {
                this.editingFolioCancelled = true;
                this.editingFolioIndex = null;
                this.editingFolioValue = '';
            }
        },

        /**
         * Cancels concept editing
         */
        cancelConceptEdit() {
            this.cancelFieldEdit('concept');
        },

        /**
         * Starts editing a folio cell
         * @param {number} index - Index of the row to edit
         */
        startEditingFolio(index) {
            this.editingFolioIndex = index;
            this.editingFolioValue = this.filteredCsvData[index].Folio;
            this.editingFolioCancelled = false;
            
            // Focus input on next tick
            this.$nextTick(() => {
                if (this.$refs.folioInput && this.$refs.folioInput[0]) {
                    this.$refs.folioInput[0].focus();
                    this.$refs.folioInput[0].select();
                }
            });
        },

        /**
         * Saves the edited folio value
         * @param {number} index - Index of the row being edited
         */
        saveFolioEdit(index) {
            this.saveFieldEdit('folio', index);
        },

        /**
         * Cancels folio editing
         */
        cancelFolioEdit() {
            this.cancelFieldEdit('folio');
        },

        /**
         * Gets the selected value for a dropdown in a specific row
         * @param {string} folio - Row identifier (Folio)
         * @param {string} field - Field name ('client', 'type', 'branch')
         * @returns {Object|null} Selected option object or null
         */
        getRowSelection(folio, field) {
            if (!this.rowSelections[folio]) {
                return null;
            }
            return this.rowSelections[folio][field] || null;
        },

        /**
         * Updates the selected value for a dropdown in a specific row
         * @param {string} folio - Row identifier (Folio)
         * @param {string} field - Field name ('legalName', 'client', 'type', 'branch', 'commissionPercentage')
         * @param {Object} value - Selected option object
         */
        updateRowSelection(folio, field, value) {
            if (!this.rowSelections[folio]) {
                this.rowSelections[folio] = {};
            }
            this.rowSelections[folio][field] = value;

            // If a legal name is selected, automatically select the corresponding client
            if (field === 'legalName' && value && value.client_id) {
                const correspondingClient = this.clients.find(client => client.id === value.client_id);
                if (correspondingClient) {
                    this.rowSelections[folio]['client'] = correspondingClient;
                }
            }
        },

        /**
         * Initializes default selections for all rows
         * Sets default type (id: 2), default branch (Sin comisión), and default commission percentage
         */
        initializeRowSelections() {
            this.filteredCsvData.forEach(row => {
                const folio = row.Folio;
                if (!this.rowSelections[folio]) {
                    // Find legal name by RFC match
                    const rowRfc = row.RFC ? row.RFC.replace(/\s+/g, '').toUpperCase() : null;
                    let matchedLegalName = null;
                    
                    if (rowRfc && this.legalNames.length > 0) {
                        matchedLegalName = this.legalNames.find(ln => {
                            const legalNameRfc = ln.rfc ? ln.rfc.replace(/\s+/g, '').toUpperCase() : null;
                            return legalNameRfc === rowRfc;
                        });
                    }
                    
                    // Find corresponding client if legal name was matched
                    let matchedClient = this.clientOptions[0]; // Default: "Selecciona un cliente"
                    if (matchedLegalName && matchedLegalName.client_id) {
                        const correspondingClient = this.clients.find(client => client.id === matchedLegalName.client_id);
                        if (correspondingClient) {
                            matchedClient = correspondingClient;
                        }
                    }

                    this.rowSelections[folio] = {
                        client: matchedClient,
                        type: this.typeOptions.find(t => t.id === DEFAULT_TYPE_ID) || this.typeOptions[0],
                        branch: this.branchOptions[0], // "Sin comisión"
                        commissionPercentage: 0,
                        legalName: matchedLegalName || null
                    };
                }
            });
        }
    }
}
</script>

<style scoped>
    /* Responsive table container */
    .table-responsive-container {
        width: 100%;
        overflow-x: auto;
        overflow-y: visible;
        min-width: 0;
        scroll-behavior: smooth;
        scrollbar-width: thin;
        scrollbar-color: #cbd5e0 #f7fafc;
    }

    /* Add border radius to wrapper */
    .table-responsive-container ::v-deep(.p-datatable-wrapper) {
        border-radius: 0.5rem;
        overflow: hidden;
    }

    /* Custom scrollbar for webkit browsers */
    .table-responsive-container::-webkit-scrollbar {
        height: 8px;
    }

    .table-responsive-container::-webkit-scrollbar-track {
        @apply bg-gray-100 dark:bg-gray-700 rounded;
    }

    .table-responsive-container::-webkit-scrollbar-thumb {
        @apply bg-gray-400 dark:bg-gray-500 rounded;
    }

    .table-responsive-container::-webkit-scrollbar-thumb:hover {
        @apply bg-gray-500 dark:bg-gray-400;
    }

    ::v-deep(.bulk-upload-table.p-datatable .p-datatable-thead > tr > th) {
        background-color: #0f1338;
        color: white;
        padding: 0.75rem 1rem;
        font-weight: 600;
        border: none;
    }

    ::v-deep(.bulk-upload-table.p-datatable .p-datatable-tbody > tr:nth-child(odd)) {
        background-color: #f3f4f6;
    }

    ::v-deep(.bulk-upload-table.p-datatable .p-datatable-tbody > tr:nth-child(even)) {
        background-color: #ffffff;
    }

    ::v-deep(.bulk-upload-table.p-datatable .p-datatable-tbody > tr > td) {
        padding: 0.75rem 1rem;
        color: #1f2937;
        border: none;
    }

    ::v-deep(.bulk-upload-table.p-datatable .p-datatable-tbody > tr:hover) {
        background-color: #fef3c7 !important;
    }

    /* Editable cell styles */
    .editable-cell {
        cursor: pointer;
        min-height: 2rem;
        padding: 0.25rem;
        border-radius: 4px;
        transition: background-color 0.2s;
    }
    .process-btn, .clear-btn, .delete-btn, .bulk-edit-btn {
        @apply rounded px-4 py-2 text-lg shadow focus:outline-none transition-colors;
    }

    .process-btn, .clear-btn {
        @apply bg-zinc-200 text-zinc-900 border border-zinc-300 hover:bg-zinc-300 
               dark:bg-zinc-700 dark:text-zinc-100 dark:border-zinc-600 dark:hover:bg-zinc-600;
    }

    .delete-btn {
        @apply bg-red-500 text-white border border-red-600 hover:bg-red-600 
               dark:bg-red-600 dark:border-red-700 dark:hover:bg-red-700;
    }

    .bulk-edit-btn {
        @apply bg-blue-500 text-white border border-blue-600 hover:bg-blue-600 
               dark:bg-blue-600 dark:border-blue-700 dark:hover:bg-blue-700;
    }

    .delete-row-btn {
        @apply text-red-600 hover:bg-red-500 hover:text-white dark:text-red-400 dark:hover:bg-red-600 dark:hover:text-white transition-colors;
        font-size: 1.1rem !important;
    }
    
    .delete-row-btn:hover {
        transform: scale(1.1);
    }

    /* Drag and drop area styles */
    .drag-drop-area {
        transition: all 0.3s ease;
    }

    .drag-drop-area.dragging {
        @apply border-blue-500 bg-blue-50 dark:bg-blue-900/20;
    }

    .drag-drop-area:hover:not(.disabled) {
        @apply bg-gray-50 dark:bg-gray-800/50 border-blue-400 dark:border-blue-500;
    }

    .drag-drop-area.disabled {
        @apply opacity-50 cursor-not-allowed;
    }

    .drag-drop-area.disabled * {
        pointer-events: none;
    }

    /* Editable cell styles */
    .editable-cell {
        cursor: pointer;
        min-height: 2rem;
        padding: 0.25rem;
        border-radius: 4px;
        transition: background-color 0.2s;
    }

    .editable-cell:hover {
        @apply bg-blue-50 dark:bg-blue-900/30;
    }

    .concept-input {
        width: 100%;
        padding: 0.5rem;
        border: 2px solid #3b82f6;
        border-radius: 4px;
        font-size: 0.875rem;
        outline: none;
        @apply bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100;
    }

    .concept-input:focus {
        border-color: #2563eb;
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }

    .folio-input {
        width: 100%;
        padding: 0.5rem;
        border: 2px solid #10b981;
        border-radius: 4px;
        font-size: 0.875rem;
        outline: none;
        font-weight: 600;
        @apply bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100;
    }

    .folio-input:focus {
        border-color: #059669;
        box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
    }

    /* Dropdown cell styles */
    .dropdown-cell {
        font-size: 0.875rem;
    }

    ::v-deep(.dropdown-cell .p-dropdown) {
        @apply text-sm;
        width: 100%;
    }

    ::v-deep(.dropdown-cell .p-dropdown-label) {
        padding: 0.4rem 0.75rem;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    /* Dropdown panel max height and scrolling */
    ::v-deep(.dropdown-legal-name .p-dropdown-panel),
    ::v-deep(.dropdown-client .p-dropdown-panel),
    ::v-deep(.dropdown-type .p-dropdown-panel),
    ::v-deep(.dropdown-branch .p-dropdown-panel) {
        max-height: 200px;
        overflow-y: auto;
    }

    /* Dropdown item text truncation for long names */
    ::v-deep(.dropdown-legal-name .p-dropdown-item),
    ::v-deep(.dropdown-client .p-dropdown-item),
    ::v-deep(.dropdown-type .p-dropdown-item),
    ::v-deep(.dropdown-branch .p-dropdown-item) {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        max-width: 100%;
    }

    /* Specific max widths for dropdown labels */
    ::v-deep(.dropdown-legal-name .p-dropdown-label) {
        max-width: 12rem;
    }

    ::v-deep(.dropdown-client .p-dropdown-label) {
        max-width: 10rem;
    }

    ::v-deep(.dropdown-type .p-dropdown-label) {
        max-width: 8rem;
    }

    ::v-deep(.dropdown-branch .p-dropdown-label) {
        max-width: 10rem;
    }

    /* Commission container styles */
    .commission-container {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
    }

    .commission-percentage-input {
        width: 100%;
    }

    ::v-deep(.commission-percentage-input .p-inputnumber) {
        width: 100%;
    }

    ::v-deep(.commission-percentage-input .p-inputnumber-input) {
        @apply text-sm;
        @apply bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100;
        @apply border-gray-300 dark:border-gray-600;
        padding: 0.4rem 0.75rem;
        width: 100%;
    }

    ::v-deep(.commission-percentage-input .p-inputnumber-input:focus) {
        @apply border-blue-500 dark:border-blue-400;
        box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
    }
</style>

<style>
/* Dark mode styles for bulk upload table */
.dark .bulk-upload-table.p-datatable .p-datatable-thead > tr > th {
    background-color: #D9D9D9;
    color: #0f1338;
}

.dark .bulk-upload-table.p-datatable .p-datatable-tbody > tr:nth-child(odd) {
    background-color: #18181b;
}

.dark .bulk-upload-table.p-datatable .p-datatable-tbody > tr:nth-child(even) {
    background-color: #27272a;
}

.dark .bulk-upload-table.p-datatable .p-datatable-tbody > tr > td {
    color: #f3f4f6;
}

.dark .bulk-upload-table.p-datatable .p-datatable-tbody > tr:hover {
    background-color: #422006 !important;
    color: white !important;
}

.dark .editable-cell:hover {
    background-color: #3b82f6 !important;
    color: white !important;
}

/* Checkbox styling for better contrast in dark mode */
.dark .bulk-upload-table.p-datatable .p-checkbox .p-checkbox-box {
    background-color: #374151;
    border-color: #6b7280;
}

.dark .bulk-upload-table.p-datatable .p-checkbox .p-checkbox-box.p-highlight {
    background-color: #3b82f6;
    border-color: #3b82f6;
}

.dark .bulk-upload-table.p-datatable .p-checkbox .p-checkbox-box .p-checkbox-icon {
    color: white;
}

/* Prevent header background change on active sort */
.bulk-upload-table.p-datatable .p-datatable-thead > tr > th.p-highlight {
    background-color: #0f1338 !important;
    color: white !important;
}

.dark .bulk-upload-table.p-datatable .p-datatable-thead > tr > th.p-highlight {
    background-color: #D9D9D9 !important;
    color: #0f1338 !important;
}

/* Sort icon contrast in dark mode */
.dark .bulk-upload-table.p-datatable .p-datatable-thead > tr > th .p-sortable-column-icon {
    color: #0f1338;
}

/* Active sort indicator */
.dark .bulk-upload-table.p-datatable .p-datatable-thead > tr > th.p-highlight .p-sortable-column-icon {
    color: #3b82f6;
}

/* Process button - light mode */
html:not(.dark) .process-btn {
    background-color: #0f1338 !important;
    border-color: #0f1338 !important;
    color: white !important;
}

html:not(.dark) .process-btn:hover:not(:disabled) {
    background-color: #1a1f4d !important;
    border-color: #1a1f4d !important;
}

/* Process button - dark mode */
html.dark .process-btn {
    background-color: #D9D9D9 !important;
    border-color: #D9D9D9 !important;
    color: #0f1338 !important;
}

html.dark .process-btn:hover:not(:disabled) {
    background-color: #c4c4c4 !important;
    border-color: #c4c4c4 !important;
}

/* Clear button - light mode */
html:not(.dark) .clear-btn {
    background-color: #0f1338 !important;
    border-color: #0f1338 !important;
    color: white !important;
}

html:not(.dark) .clear-btn:hover:not(:disabled) {
    background-color: #1a1f4d !important;
    border-color: #1a1f4d !important;
}

/* Clear button - dark mode */
html.dark .clear-btn {
    background-color: #D9D9D9 !important;
    border-color: #D9D9D9 !important;
    color: #0f1338 !important;
}

html.dark .clear-btn:hover:not(:disabled) {
    background-color: #c4c4c4 !important;
    border-color: #c4c4c4 !important;
}
</style>